import React from 'react';
import { Column } from '../../../components/DataTable/DataTable';
import { createGetTableActions, getAccountingToolbarIcon } from '../shared/tableActions';
import Badge from '../../../components/ui/Badge';
import { LockIcon } from '../../../components/ui';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { APP_CONFIG, formatDate as formatDateGlobal } from '../../../config/appConfig';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../contexts/ThemeContext';
import { mapCodeNameSelectOption, mapCombLevelSelectOption } from '../shared/formFieldHelpers';
import type { AccCompany } from './useData';

type AccCompanyUser = NonNullable<AccCompany['createdByUser']>;

const formatCodeName = (entity?: { code: string; name: string; combLevel?: string } | null) => {
  if (!entity) return '-';
  return entity.name || entity.code || '-';
};

const formatUserLabel = (user?: AccCompanyUser | null) => {
  if (!user) return '-';
  const name =
    user.userDisplayName?.trim() ||
    `${user.userFirstName ?? ''} ${user.userLastName ?? ''}`.trim();
  return name || user.userEmail || '-';
};

export const companyIsUsedInRecords = (item: AccCompany) => item.usedInRecords === true;

export const companyCanDelete = (item: AccCompany) => !companyIsUsedInRecords(item);

const toCompanyPayload = (data: Record<string, unknown>) => ({
  name: data.name,
  code: data.code,
  intercompanyAccounts: data.intercompanyAccounts ? String(data.intercompanyAccounts).trim() : null,
  managementCompanyId:
    data.isVessel && data.managementCompanyId ? Number(data.managementCompanyId) : null,
  isVessel: !!data.isVessel,
  isManagementCompany: !!data.isManagementCompany,
  isActive: data.isActive ?? true});

export const moduleConfig = {
  module: {
    title: 'Companies',
    permissionModuleKey: 'acc_companies',
    route: {
      list: '/accounting/companies',
      new: '/accounting/companies/new',
      detail: (id: number) => `/accounting/companies/${id}`,
      edit: (id: number) => `/accounting/companies/${id}/edit`},
    api: { list: '/acc-companies', detail: (id: number) => `/acc-companies/${id}` }},
  pagination: { initialItemsPerPage: APP_CONFIG.table.defaultPageSize, itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions },
  search: { placeholder: 'Search companies…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: AccCompany[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New company', showTitle: false, variant: 'primary' as const, href: '/accounting/companies/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: AccCompany) => item.id,
  },
  emptyStates: { noData: 'Create a company.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete company',
    message: 'Delete this company?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deleteAccCompany } = await import('../../../services/mutations');
      await deleteAccCompany(id);
    }},
  utils: { formatDate: (d?: string) => (d ? formatDateGlobal(d) : '') },
  detail: {
    subtitle: 'Company',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Company not found.',
    notFoundBackText: 'Back',
    defaultTitle: 'Company',
    getTitle: (item: AccCompany) => item.name},
  form: {
    new: { title: 'New company', subtitle: 'Create a legal entity or vessel', submitText: 'Create' },
    edit: { title: 'Edit company', subtitle: 'Update identification, role, and accounting settings', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    formSizing: 'content' as const,
    formMaxWidth: 'max-w-3xl',
    create: async (data: Partial<AccCompany>) => {
      const { createAccCompany } = await import('../../../services/mutations');
      return createAccCompany(toCompanyPayload(data as Record<string, unknown>));
    },
    update: async (id: number, data: Partial<AccCompany>) => {
      const { updateAccCompany } = await import('../../../services/mutations');
      return updateAccCompany(id, toCompanyPayload(data as Record<string, unknown>));
    }},
  fields: [
    {
      name: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'e.g. VSL01',
      validation: { required: true, maxLength: 30 }},
    {
      name: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. MV Atlantic Star',
      validation: { required: true, maxLength: 100 }},
    {
      name: 'intercompanyAccounts',
      label: 'Intercompany accounts',
      type: 'select',
      placeholder: 'None',
      helperText: 'Ledger account combLevel used for balances between companies.',
      optionsEndpointFromFormData: () => '/acc-accounts?postingOnly=true',
      valueField: 'combLevel',
      mapApiItemToSelectOption: mapCombLevelSelectOption,
      options: [{ value: '', label: 'None' }]},
    {
      name: 'isVessel',
      label: 'Vessel',
      type: 'switch',
      switchLabel: 'This company represents a vessel',
      description: 'Enable for ship-level entities. Vessels can be linked to a management company.',
      defaultValue: false,
      onChangeSideEffect: (value, setFieldValue) => {
        if (value) {
          setFieldValue('isManagementCompany', false);
        } else {
          setFieldValue('managementCompanyId', '');
        }
      }},
    {
      name: 'managementCompanyId',
      label: 'Management company',
      type: 'select',
      placeholder: 'Select management company',
      helperText: 'The shore-based company that manages this vessel.',
      optionsEndpointFromFormData: () => '/acc-companies',
      valueField: 'id',
      mapApiItemToSelectOption: mapCodeNameSelectOption,
      filterDynamicOptionItem: (item, formData) =>
        item.isManagementCompany === true &&
        item.isActive !== false &&
        (!formData.id || Number(item.id) !== Number(formData.id)),
      options: [{ value: '', label: 'None' }]},
    {
      name: 'isManagementCompany',
      label: 'Management company',
      type: 'switch',
      switchLabel: 'Acts as a management company',
      description: 'Enable for shore-based companies that manage one or more vessels.',
      defaultValue: false,
      onChangeSideEffect: (value, setFieldValue) => {
        if (value) {
          setFieldValue('isVessel', false);
          setFieldValue('managementCompanyId', '');
        }
      }},
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      switchLabel: 'Active',
      description: 'Inactive companies are hidden from journal lines and selection lists.',
      defaultValue: true},
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'identification',
        title: 'Identification',
        description: 'Company code and name.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['code', 'name'],
        fieldSpans: {
          code: 'md:col-span-4',
          name: 'md:col-span-8'},
        collapsible: true,
        defaultCollapsed: true},
      {
        id: 'company-role',
        title: 'Company type',
        description: 'A company can be a vessel, a management company, or a standard legal entity.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isVessel', 'isManagementCompany'],
        fieldSpans: {
          isVessel: 'md:col-span-12',
          isManagementCompany: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: true},
      {
        id: 'vessel-management',
        title: 'Vessel management',
        description: 'Link this vessel to the shore-based company that manages it.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['managementCompanyId'],
        fieldSpans: {
          managementCompanyId: 'md:col-span-12'},
        collapsible: false,
        visibleWhen: (formData) => !!formData.isVessel},
      {
        id: 'accounting',
        title: 'Accounting',
        description: 'Default ledger account for intercompany balances.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['intercompanyAccounts'],
        fieldSpans: {
          intercompanyAccounts: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: true},
      {
        id: 'status',
        title: 'Status',
        description: 'Control whether this company appears in posting and selection lists.',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['isActive'],
        fieldSpans: {
          isActive: 'md:col-span-12'},
        collapsible: true,
        defaultCollapsed: true},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

export const getColumns = (formatDate: (d?: string) => string): Column<AccCompany>[] => [
  { key: 'code', header: 'Code', sortable: true, render: (v) => <TextCell value={v} /> },
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    render: (v, row) => (
      <span className="inline-flex items-center gap-1.5 min-w-0">
        <TextCell value={v} />
        {row?.usedInRecords && (
          <span title="Used in journal records" className="inline-flex text-amber-600 shrink-0">
            <LockIcon className="w-4 h-4" />
          </span>
        )}
      </span>
    )},
  {
    key: 'isVessel',
    header: 'Vessel',
    render: (v) => <Badge variant={v ? 'info' : 'default'}>{v ? 'Yes' : 'No'}</Badge>},
  {
    key: 'isManagementCompany',
    header: 'Management role',
    render: (v) => <Badge variant={v ? 'accent' : 'default'}>{v ? 'Yes' : 'No'}</Badge>},
  {
    key: 'managementCompany',
    header: 'Managed by',
    render: (_v, row) => <TextCell value={row?.isVessel ? formatCodeName(row?.managementCompany) : '—'} />},
  {
    key: 'intercompanyAccount',
    header: 'Intercompany accounts',
    render: (_v, row) => <TextCell value={formatCodeName(row?.intercompanyAccount)} />},
  {
    key: 'isActive',
    header: 'Active',
    render: (v) => <Badge variant={v ? 'success' : 'danger'}>{v ? 'Yes' : 'No'}</Badge>},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
  { key: 'updatedAt', header: 'Updated', render: (v) => formatDate(v) },
  {
    key: 'createdByUser',
    header: 'Created by',
    render: (_v, row) => <TextCell value={formatUserLabel(row?.createdByUser)} />},
  {
    key: 'updatedByUser',
    header: 'Updated by',
    render: (_v, row) => <TextCell value={formatUserLabel(row?.updatedByUser)} />},
];

export const getDetailSections = (item: AccCompany, formatDate: (s?: string) => string) => [
  {
    title: 'Identification',
    fields: [
      { label: 'Code', value: item.code },
      { label: 'Name', value: item.name },
    ]},
  {
    title: 'Company type',
    fields: [
      { label: 'Vessel', value: item.isVessel ? 'Yes' : 'No' },
      { label: 'Management company role', value: item.isManagementCompany ? 'Yes' : 'No' },
      {
        label: 'Managed by',
        value: item.managementCompany
          ? formatCodeName(item.managementCompany)
          : item.isVessel
            ? '-'
            : 'N/A'},
    ]},
  {
    title: 'Accounting',
    fields: [
      {
        label: 'Intercompany accounts',
        value: formatCodeName(item.intercompanyAccount)},
      { label: 'Active', value: item.isActive ? 'Yes' : 'No' },
      { label: 'Created', value: formatDate(item.createdAt) },
      { label: 'Updated', value: formatDate(item.updatedAt) },
      { label: 'Created by', value: formatUserLabel(item.createdByUser) },
      { label: 'Updated by', value: formatUserLabel(item.updatedByUser) },
    ]},
];

export const getTableActions = createGetTableActions<AccCompany>(moduleConfig.module.route, {
  showDelete: (item) => companyCanDelete(item)});

export const getToolbarIcon = getAccountingToolbarIcon;

const StatusBadges: React.FC<{ item: AccCompany }> = ({ item }) => (
  <>
    {item.isVessel && <Badge variant="info">Vessel</Badge>}
    {item.isManagementCompany && <Badge variant="accent">Management</Badge>}
    {!item.isVessel && !item.isManagementCompany && <Badge variant="default">Entity</Badge>}
    <Badge variant={item.isActive ? 'success' : 'danger'} className="ml-2">
      {item.isActive ? 'Active' : 'Inactive'}
    </Badge>
  </>
);

export const getStatusBadges = (item: AccCompany) => <StatusBadges item={item} />;
export const getInitialFormData = (): Partial<AccCompany> => ({
  name: '',
  code: '',
  intercompanyAccounts: '',
  managementCompanyId: '' as unknown as number,
  isVessel: false,
  isManagementCompany: false,
  isActive: true});
export const mapItemToFormData = (item: AccCompany): Partial<AccCompany> => ({
  id: item.id,
  name: item.name,
  code: item.code,
  intercompanyAccounts: item.intercompanyAccounts ?? '',
  managementCompanyId: (item.managementCompanyId ? String(item.managementCompanyId) : '') as unknown as number,
  isVessel: item.isVessel,
  isManagementCompany: item.isManagementCompany,
  isActive: item.isActive ?? true});
